import { BASE_URL } from '../constants';
import { getAuthHeader } from '../utils';

/**
 * @typedef Client
 * @property {number} id
 * @property {string} name
 * @property {string} currency
 * @property {boolean} can_edit_scale
 */

/**
 * @typedef Subsidiary
 * @property {number} id
 * @property {string} name
 * @property {number} company_client
 */

/**
 * Gets list of insurance clients
 * @param {boolean | undefined} scaleNull
 */
export async function getClients(scaleNull, fundType) {
  try {
    // Build params
    const params = new URLSearchParams();
    if (scaleNull !== undefined) {
      const stringValue = String(scaleNull);
      if (stringValue !== 'true' && stringValue !== 'false') {
        throw new Error('Invalid scaleNull param');
      }
      params.append('scale__isnull', scaleNull);
    }
    if (fundType) {
      params.append('fund_type', fundType);
    }
    // Search data
    const response = await fetch(BASE_URL + 'insurance/client/?' + params.toString(), {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error('Error fetching clients');
    }

    const data = await response.json();
    /**@type {Client[]} */
    const results = data.results;
    return results;
  } catch (error) {
    console.error('Error fetching clients', error);
    return [];
  }
}

/**
 * Obtener un cliente por ID
 * @author gabrielm
 * @param {number} clientId
 */
export async function getClient(clientId) {
  try {
    if (!clientId) {
      throw new Error('ID inválido');
    }

    const response = await fetch(BASE_URL + 'insurance/client/' + clientId + '/', {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(response.statusText);
    }

    const data = await response.json();
    /**@type {Client} */
    const result = data;
    return result;
  } catch (error) {
    console.error('Error fetching client', error);
    return null;
  }
}

/**
 * Get list of clients that work with a specific scale
 * @param {number} scaleId
 */
export async function getClientsOfScale(scaleId) {
  try {
    if (!scaleId) {
      throw new Error('Invalid scaleId');
    }

    const params = new URLSearchParams();
    params.append('scale', scaleId);

    const response = await fetch(BASE_URL + 'insurance/client/?' + params.toString(), {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error('Error fetching clients');
    }

    const data = await response.json();
    /**@type {Client[]} */
    const results = data.results;
    return results;
  } catch (error) {
    console.error('Error fetching clients', error);
    return [];
  }
}

/**
 * Get list of subsidiaries (filiales)
 */
export async function getSubsidiaries() {
  try {
    // Search data
    const response = await fetch(BASE_URL + 'insurance/subsidiary/', {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error('Error fetching subsidiaries');
    }

    const data = await response.json();
    /**@type {Subsidiary[]} */
    const results = data.results;
    return results;
  } catch (error) {
    // Se propaga el error para que TanStack Query marque la query como
    // fallida y la vista pueda ofrecer "Reintentar". Antes devolvía []
    // (catch silencioso) y un fallo de red se veía como "no hay filiales".
    console.error('Error fetching subsidiaries', error);
    throw error;
  }
}

/**
 * Create a subsidiary (filial)
 * @param {{ name: string, company_client: number }} data
 */
export async function createSubsidiary(data) {
  try {
    const response = await fetch(`${BASE_URL}insurance/subsidiary/`, {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Error creating subsidiary');
    }
    const respData = await response.json();
    return {
      type: 'success',
      message: 'Filial creada correctamente',
      data: respData,
    };
  } catch (error) {
    console.error('Error creating subsidiary', error);
    return { type: 'error', message: 'Ocurrió un error al crear la filial' };
  }
}

/**
 * Update a subsidiary (filial)
 * @param {object} params
 * @param {number} params.subsidiaryId
 * @param {{ name?: string, company_client?: number }} params.data
 */
export async function updateSubsidiary({ subsidiaryId, data }) {
  try {
    const response = await fetch(`${BASE_URL}insurance/subsidiary/${subsidiaryId}/`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Error updating subsidiary');
    }
    const respData = await response.json();
    return {
      type: 'success',
      message: 'Filial actualizada correctamente',
      data: respData,
    };
  } catch (error) {
    console.error('Error updating subsidiary', error);
    return {
      type: 'error',
      message: 'Ocurrió un error al actualizar la filial',
    };
  }
}

/**
 * Delete a subsidiary (filial) by id.
 * Returns true on success, null on error.
 * @param {number|string} subsidiaryId
 */
export async function deleteSubsidiary(subsidiaryId) {
  try {
    const headers = { ...(getAuthHeader() || {}) };
    const response = await fetch(`${BASE_URL}insurance/subsidiary/${subsidiaryId}/`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      let bodyText = null;
      try {
        bodyText = await response.text();
      } catch (readErr) {
        bodyText = `<unable to read body: ${readErr.message}>`;
      }
      console.error(
        `deleteSubsidiary: non-OK response ${response.status} ${response.statusText}`,
        bodyText
      );
      return null;
    }
    return true;
  } catch (error) {
    console.error('Error deleting subsidiary', error?.message || error);
    return null;
  }
}

export async function updateClient({ clientId, data }) {
  try {
    const response = await fetch(`${BASE_URL}insurance/client/${clientId}/`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Error updating client');
    }
    return {
      type: 'success',
      message: 'Cliente actualizado correctamente',
    };
  } catch (error) {
    console.error('Error updating client', error);
    return { type: 'error', message: 'Ocurrió un error' };
  }
}

export async function createClient(data) {
  try {
    const response = await fetch(`${BASE_URL}insurance/client/`, {
      method: 'POST',
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Error creating client');
    }
    const respData = await response.json();
    return {
      type: 'success',
      message: 'Cliente creado correctamente',
      data: respData,
    };
  } catch (error) {
    console.error('Error creating client', error);
    return { type: 'error', message: 'Ocurrió un error al crear el cliente' };
  }
}

/**
 * Delete a client by id.
 * Returns true on success, null on error.
 * @param {number|string} clientId
 */
export async function deleteClient(clientId) {
  try {
    const headers = { ...(getAuthHeader() || {}) };
    const response = await fetch(`${BASE_URL}insurance/client/${clientId}/`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      let bodyText = null;
      try {
        bodyText = await response.text();
      } catch (readErr) {
        bodyText = `<unable to read body: ${readErr.message}>`;
      }
      console.error(
        `deleteClient: non-OK response ${response.status} ${response.statusText}`,
        bodyText
      );
      return null;
    }
    return true;
  } catch (error) {
    console.error('Error deleting client', error?.message || error);
    return null;
  }
}
